<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-semibold">{{ t('apiKeys') }}</h3>
      <CVButton
        v-if="$can('create', 'ApiKey')"
        :is-loading="isCreating"
        @click="handleShowCreateModal"
      >
        {{ t('createApiKey') }}
      </CVButton>
    </div>

    <div v-if="isLoading" class="flex justify-center py-4">
      <Spinner class="size-6" />
    </div>

    <div v-else-if="!apiKeys || apiKeys.length === 0" class="text-sm text-gray-500">
      {{ t('noApiKeys') }}
    </div>

    <div v-else class="space-y-3">
      <div v-for="apiKey in apiKeys" :key="apiKey.id" :class="card()">
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center gap-2">
              <h4 class="font-medium">{{ apiKey.name || t('unnamedApiKey') }}</h4>
              <span class="font-mono text-sm text-gray-500">{{ apiKey.keyPrefix }}****</span>
            </div>
            <div class="mt-2 space-y-1 text-sm text-gray-600">
              <div>{{ t('createdAt') }}: {{ formatDate(apiKey.createdDate, getDateFormat()) }}</div>
              <div v-if="apiKey.lastUsedAt">
                {{ t('lastUsedAt') }}: {{ formatDate(apiKey.lastUsedAt, getDateFormat()) }}
              </div>
              <div v-else class="text-gray-400">{{ t('neverUsed') }}</div>
            </div>
          </div>
          <CVButton
            v-if="$can('delete', 'ApiKey')"
            variant="danger"
            size="sm"
            :is-loading="deletingId === apiKey.id"
            @click="handleDelete(apiKey)"
          >
            {{ t('delete') }}
          </CVButton>
        </div>
      </div>
    </div>

    <!-- Name Input Modal -->
    <Modal v-model="showNameDialog" :content-class="'max-w-md'" :close-on-click-outside="false">
      <template #title>
        <h3 class="text-lg font-semibold">{{ t('createApiKey') }}</h3>
      </template>
      <form class="space-y-4" @submit.prevent="handleCreate">
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700">{{ t('apiKeyName') }}</label>
          <Input v-model="apiKeyName" :placeholder="t('apiKeyNamePlaceholder')" class="w-full" />
        </div>
        <div class="flex justify-end gap-2">
          <CVButton variant="secondary" type="button" @click="showNameDialog = false">
            {{ t('cancel') }}
          </CVButton>
          <CVButton
            :is-loading="isCreating"
            variant="default"
            :disabled="!apiKeyName || apiKeyName.trim() === ''"
            type="submit"
          >
            {{ t('create') }}
          </CVButton>
        </div>
      </form>
    </Modal>

    <!-- Show Plain Key Modal (only shown once after creation) -->
    <Modal v-model="showPlainKeyDialog" :content-class="'max-w-2xl'" :close-on-click-outside="true">
      <template #title>
        <h3 class="text-lg font-bold">{{ t('apiKeyCreated') }}</h3>
      </template>
      <div class="space-y-4">
        <div>
          <p class="mb-2 text-sm text-gray-700">{{ t('apiKeyWarning') }}</p>
          <div class="relative">
            <Input v-if="newlyCreatedKey" :value="newlyCreatedKey.plainKey" readonly />
            <button
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              @click="copyToClipboard"
            >
              <ClipboardDocumentIcon class="h-5 w-5" />
            </button>
          </div>
        </div>
        <div class="flex justify-end">
          <CVButton @click="showPlainKeyDialog = false">{{ t('close') }}</CVButton>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import CVButton from '@/components/buttons/CVButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import Modal from '@/components/Modal.vue';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { getDateFormat } from '@/hooks/use-date-format.ts';
import { Alert } from '@/services/alert.service';
import type { ApiKey, CreateApiKeyResponseDto } from '@carvia/ros-client-types';
import { useApiKeysByUserId, useCreateApiKey, useDeleteApiKey } from '@/queries/use-api-keys';
import { ClipboardDocumentIcon } from '@heroicons/vue/24/outline';
import { createToast } from 'mosha-vue-toastify';
import { card } from '@carvia/theme/lib/cva/card';
import Input from '../../../components/Input.vue';

const props = defineProps<{
  userId: string;
}>();

const { t } = useI18n();

const deletingId = ref<string | null>(null);
const showPlainKeyDialog = ref(false);
const showNameDialog = ref(false);
const apiKeyName = ref('');
const newlyCreatedKey = ref<CreateApiKeyResponseDto | null>(null);

const { data: apiKeys, isLoading } = useApiKeysByUserId(() => props.userId);
const createApiKeyMutation = useCreateApiKey();
const deleteApiKeyMutation = useDeleteApiKey();

const isCreating = computed(() => createApiKeyMutation.isPending.value);

const handleShowCreateModal = () => {
  apiKeyName.value = '';
  showNameDialog.value = true;
};

const handleCreate = async () => {
  if (!apiKeyName.value || apiKeyName.value.trim() === '' || isCreating.value) return;
  const result = await createApiKeyMutation.mutateAsync({
    userId: props.userId,
    name: apiKeyName.value.trim(),
  });
  newlyCreatedKey.value = result;
  showNameDialog.value = false;
  apiKeyName.value = '';
  showPlainKeyDialog.value = true;
};

const handleDelete = async (apiKey: ApiKey) => {
  const result = await Alert.fire({
    titleText: t('deleteApiKey'),
    text: t('deleteApiKeyConfirm'),
    icon: 'warning',
    showDenyButton: true,
    showCancelButton: true,
    showConfirmButton: false,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });

  if (!result.isDenied) return;

  await deleteApiKeyMutation.mutateAsync({ id: apiKey.id, userId: props.userId });
};

const copyToClipboard = async () => {
  if (!newlyCreatedKey.value) return;
  try {
    await navigator.clipboard.writeText(newlyCreatedKey.value.plainKey);
    createToast(t('copiedToClipboard'), {
      position: 'bottom-right',
      type: 'success',
    });
  } catch (error) {
    createToast(t('failedToCopy'), {
      position: 'bottom-right',
      type: 'danger',
    });
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "apiKeys": "API Keys",
    "createApiKey": "Create API Key",
    "noApiKeys": "No API keys found",
    "unnamedApiKey": "Unnamed API Key",
    "active": "Active",
    "inactive": "Inactive",
    "createdAt": "Created",
    "lastUsedAt": "Last Used",
    "neverUsed": "Never used",
    "delete": "Delete",
    "create": "Create",
    "cancel": "Cancel",
    "apiKeyCreated": "API Key Created",
    "apiKeyWarning": "This is your API key. Store it securely - you won't be able to see it again!",
    "copyToClipboard": "Copy to Clipboard",
    "close": "Close",
    "deleteApiKey": "Delete API Key",
    "deleteApiKeyConfirm": "Are you sure you want to delete this API key? This action cannot be undone.",
    "apiKeyDeleted": "API Key deleted successfully",
    "error": "Error",
    "failedToLoadApiKeys": "Failed to load API keys",
    "failedToCreateApiKey": "Failed to create API key",
    "failedToDeleteApiKey": "Failed to delete API key",
    "copiedToClipboard": "Copied to clipboard",
    "failedToCopy": "Failed to copy to clipboard",
    "apiKeyName": "Please enter a name for the API key that will be used to identify it.",
    "apiKeyNamePlaceholder": "e.g. Portal Integration"
  },
  "de": {
    "apiKeys": "API-Schlüssel",
    "createApiKey": "API-Schlüssel erstellen",
    "noApiKeys": "Keine API-Schlüssel gefunden",
    "unnamedApiKey": "Unbenannter API-Schlüssel",
    "active": "Aktiv",
    "inactive": "Inaktiv",
    "createdAt": "Erstellt",
    "lastUsedAt": "Zuletzt verwendet",
    "neverUsed": "Nie verwendet",
    "delete": "Löschen",
    "create": "Erstellen",
    "cancel": "Abbrechen",
    "apiKeyCreated": "API-Schlüssel erstellt",
    "apiKeyWarning": "Dies ist Ihr API-Schlüssel. Speichern Sie ihn sicher - Sie werden ihn nicht mehr sehen können!",
    "copyToClipboard": "In Zwischenablage kopieren",
    "close": "Schließen",
    "deleteApiKey": "API-Schlüssel löschen",
    "deleteApiKeyConfirm": "Möchten Sie diesen API-Schlüssel wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
    "apiKeyDeleted": "API-Schlüssel erfolgreich gelöscht",
    "error": "Fehler",
    "failedToLoadApiKeys": "Fehler beim Laden der API-Schlüssel",
    "failedToCreateApiKey": "Fehler beim Erstellen des API-Schlüssels",
    "failedToDeleteApiKey": "Fehler beim Löschen des API-Schlüssels",
    "copiedToClipboard": "In Zwischenablage kopiert",
    "failedToCopy": "Fehler beim Kopieren in die Zwischenablage",
    "apiKeyName": "Bitte geben Sie einen Namen für den API-Schlüssel ein, der es Ihnen ermöglicht, ihn zu identifizieren.",
    "apiKeyNamePlaceholder": "z.B. Portal-Integration"
  },
  "es": {
    "apiKeys": "Claves API",
    "createApiKey": "Crear clave API",
    "noApiKeys": "No se encontraron claves API",
    "unnamedApiKey": "Clave API sin nombre",
    "active": "Activa",
    "inactive": "Inactiva",
    "createdAt": "Creada",
    "lastUsedAt": "Último uso",
    "neverUsed": "Nunca usada",
    "delete": "Eliminar",
    "create": "Crear",
    "cancel": "Cancelar",
    "apiKeyCreated": "Clave API creada",
    "apiKeyWarning": "Esta es su clave API. Guárdela de forma segura: no podrá volver a verla.",
    "copyToClipboard": "Copiar al portapapeles",
    "close": "Cerrar",
    "deleteApiKey": "Eliminar clave API",
    "deleteApiKeyConfirm": "¿Seguro que desea eliminar esta clave API? Esta acción no se puede deshacer.",
    "apiKeyDeleted": "Clave API eliminada correctamente",
    "error": "Error",
    "failedToLoadApiKeys": "No se pudieron cargar las claves API",
    "failedToCreateApiKey": "No se pudo crear la clave API",
    "failedToDeleteApiKey": "No se pudo eliminar la clave API",
    "copiedToClipboard": "Copiado al portapapeles",
    "failedToCopy": "No se pudo copiar al portapapeles",
    "apiKeyName": "Introduzca un nombre para identificar la clave API.",
    "apiKeyNamePlaceholder": "p. ej. Integración portal"
  }
}
</i18n>
